<template>
  <div class="container-fluid py-4">
    <!-- Header with Tabs and Create Button -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <!-- Tabs -->
      <ul class="nav nav-tabs" role="tablist">
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'default' }"
            @click="setActiveTab('default')"
            type="button"
          >
            Default
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            class="nav-link"
            :class="{ active: activeTab === 'lookup' }"
            @click="setActiveTab('lookup')"
            type="button"
          >
            By Users
          </button>
        </li>
      </ul>

      <!-- Create Button (only show for Default tab) -->
      <button
        v-if="activeTab === 'default'"
        type="button"
        class="btn btn-primary"
        @click="openCreateModal"
        :disabled="!canCreate('main')"
      >
        <i class="fa fa-plus"></i> Create New
      </button>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <div class="tab-pane fade show active">
        <CommonTable2
          :tableObj="tableConfig"
          :title="currentTitle"
          permissionKey="main"
          @edit="handleEdit"
          @delete="handleDelete"
          @view="handleView"
        />
      </div>
    </div>
  </div>

  <!-- pagination -->
<div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">

  <!-- Rows per page -->
  <div class="d-flex align-items-center gap-2">
    <span class="text-muted">Rows per page:</span>
    <select
      class="form-select form-select-sm rows-select"
      v-model="tablePagination.per_page"
      @change="changeRows"
    >
      <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
        {{ n }}
      </option>
    </select>
  </div>

  <!-- Pagination Buttons -->
  <div class="pagination-mini">

    <!-- Previous -->
    <button
      class="page-btn prev_btn"
      :disabled="!tablePagination.has_prev"
      @click="handlePageChange(tablePagination.current_page - 1)"
    >
      «
    </button>

    <!-- Numbered pages -->
    <button
      v-for="(p, index) in visiblePages"
      :key="index"
      class="page-btn"
      :class="{
        'page-active': p === tablePagination.current_page,
        'disabled-dots': p === '...',
      }"
      :disabled="p === '...'"
      @click="p !== '...' && typeof(p) === 'number'? handlePageChange(p) : '...'"
    >
      {{ p }}
    </button>

    <!-- Next -->
    <button
      class="page-btn next_btn"
      :disabled="!tablePagination.has_next"
      @click="handlePageChange(tablePagination.current_page + 1)"
    >
      »
    </button>

  </div>
</div>


  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    @shown="onCreateModalShown"
    :save-button-disabled="isCreateFormValid"
    dialog-class="modal-lg"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    @shown="onEditModalShown"
    :show-save-button="!isViewMode"
    :save-button-disabled="isEditFormValid"
    dialog-class="modal-lg"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue"; 
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm from "@/components/commen/modal/BaseForm.vue"; 
import apiService from "@/services/apiService"; 
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canCreate } = usePermissions();

const API_RESOURCE = "/common/module-variables";
const MODULE_FILTER = "people";
const pageTitle = "People";


// Tab management
const activeTab = ref<"default" | "lookup">("default");
const moduleData = ref<any[]>([]);
const promptVariables = ref<any[]>([]);
const isLoading = ref(false);

// Computed title based on active tab
const currentTitle = computed(() =>
  activeTab.value === "default"
    ? "Default People Variables"
    : "By Users People Variables"
);

// --- Pagination State ---
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});
const currentPage = ref(1);

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});



// Table configuration with conditional actions
const tableConfig = computed(() => ({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Description", field: "description", sortable: true },
    { name: "Type", field: "variable_type_title", sortable: true },
    { name: "Data Items", field: "data_count", sortable: false },
    { name: "Created", field: "createdAt", sortable: true, type: "date" },
  ],
  data: processedData.value,
  actions: activeTab.value === "default" ? [true] : [false], // No actions for lookup
  loading: isLoading.value,
  showCategoryFilter: false,
  showStatusFilter: false,
  table_width: "max-content",
}));

const changeRows = async () => {
  currentPage.value = 1;
  await fetchModules(1, tablePagination.value.per_page);
};

const handlePageChange = async (page:number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (page > tablePagination.value.total_pages) return;

  currentPage.value = page;
  await fetchModules(page, tablePagination.value.per_page);
};


// Process data to add computed fields
const processedData = computed(() => {
  return moduleData.value.map((item: any) => ({
    ...item,
    data_count: Array.isArray(item.data) ? item.data.length : 0,
    variable_type_title: item.variable_type_id?.title || "N/A",
  }));
});

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  module: MODULE_FILTER,
  title: "",
  description: "",
  variable_type_id: null as string | null,
  is_filterable: false,
  data: [] as string[],
  dataDisplay: "",
});

const editingItemData = ref({
  _id: "",
  module: MODULE_FILTER,
  title: "",
  description: "",
  variable_type_id: null as string | null,
  is_filterable: false,
  data: [] as string[],
  dataDisplay: "",
});

// --- Form Fields ---
const createFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
  },
  {
    key: "description",
    label: "Description",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter description",
  },
  {
    key: "variable_type_id",
    label: "Type",
    type: "select" as const,
    required: false,
    placeholder: "Select type",
    options: promptVariables.value,
  },
  {
    key: "is_filterable",
    label: "Filterable",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "dataDisplay",
    label: "Data Items (comma-separated)",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter data items separated by commas",
  },
]);

const editFormFields = computed(() => [
  {
    key: "title",
    label: "Title",
    type: "text" as const,
    required: true,
    placeholder: "Enter title",
    readonly: isViewMode.value,
  },
  {
    key: "description",
    label: "Description",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter description",
    readonly: isViewMode.value,
  },
  {
    key: "variable_type_id",
    label: "Type",
    type: "select" as const,
    required: false,
    placeholder: "Select type",
    options: promptVariables.value,
    readonly: isViewMode.value,
  },
  {
    key: "is_filterable",
    label: "Filterable",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
  {
    key: "dataDisplay",
    label: "Data Items (comma-separated)",
    type: "textarea" as const,
    required: false,
    placeholder: "Enter data items separated by commas",
    readonly: isViewMode.value,
  },
]);

// Tab management functions
const setActiveTab = (tab: "default" | "lookup") => {
  activeTab.value = tab;
  tablePagination.value.current_page = 1;
  fetchModules(1, tablePagination.value.per_page);
};

// Fetch modules based on active tab
const fetchModules = async (page = 1, limit = 10) => {
  isLoading.value = true;
  try {
    const workspaceParam = activeTab.value === "default" ? "false" : "true";
    const response = await apiService.get(`${API_RESOURCE}/workspace-scope`, {
      module: MODULE_FILTER,
      workspace: workspaceParam,
      page,
      limit,
    });

    // Handle different response structures
    if (Array.isArray(response)) {
      // Direct array response (non-paginated)
      moduleData.value = response;
      tablePagination.value.current_page = 1;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = response.length;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    } else if (Array.isArray(response.data)) {
      // Paginated response with data property
      moduleData.value = response.data;
      
      // backend gives pagination object
      if (response.pagination) {
        tablePagination.value.current_page = response.pagination.current_page || page;
        tablePagination.value.total_pages = response.pagination.total_pages || 1;
        tablePagination.value.total = response.pagination.total || response.data.length;
        tablePagination.value.has_prev = (response.pagination.current_page || page) > 1;
        tablePagination.value.has_next =
          (response.pagination.current_page || page) < (response.pagination.total_pages || 1);
      } else {
        // Fallback if no pagination metadata
        tablePagination.value.current_page = page;
        tablePagination.value.total_pages = 1;
        tablePagination.value.total = response.data.length;
        tablePagination.value.has_prev = false;
        tablePagination.value.has_next = false;
      }
    } else {
      moduleData.value = [];
      tablePagination.value.current_page = 1;
      tablePagination.value.total_pages = 1;
      tablePagination.value.total = 0;
      tablePagination.value.has_prev = false;
      tablePagination.value.has_next = false;
    }
  } catch (error) {
    console.error("Error fetching modules:", error);
    moduleData.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Fetch variable types for the Type selector
const loadPromptVariables = async () => {
  try {
    const response = await apiService.get("/common/variable-types");
    if (Array.isArray(response)) {
      promptVariables.value = response;
    } else if (response && response.data && Array.isArray(response.data)) {
      promptVariables.value = response.data;
    }
  } catch (error) {
    console.error("Error fetching variable types:", error);
  }
};

// Modal management
const openCreateModal = () => {
  isCreateModalOpen.value = true;
};

// --- Data conversion watchers ---
watch(
  () => newItemData.value.dataDisplay,
  (newVal) => {
    if (newVal) {
      newItemData.value.data = newVal
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      newItemData.value.data = [];
    }
  }
);

watch(
  () => editingItemData.value.dataDisplay,
  (newVal) => {
    if (newVal) {
      editingItemData.value.data = newVal
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      editingItemData.value.data = [];
    }
  }
);

// --- Validation ---
const isCreateFormValid = computed(() => !newItemData.value.title.trim());

const isEditFormValid = computed(() => !editingItemData.value.title?.trim());

// --- Handlers ---
const handleEdit = async (row: any) => {
  // Only allow edit for default tab
  if (activeTab.value !== "default") return;

  isViewMode.value = false;
  const dataArray = Array.isArray(row.data) ? [...row.data] : [];
  
  // Extract ID from nested variable_type_id object if necessary
  const typeId = row.variable_type_id?._id || row.variable_type_id || null;

  editingItemData.value = {
    ...row,
    variable_type_id: typeId,
    data: dataArray,
    dataDisplay: dataArray.join(", "),
  };
  isEditModalOpen.value = true;
};

const handleView = async (row: any) => {
  isViewMode.value = true;
  const dataArray = Array.isArray(row.data) ? [...row.data] : [];
  
  // Extract ID from nested variable_type_id object if necessary
  const typeId = row.variable_type_id?._id || row.variable_type_id || null;

  editingItemData.value = {
    ...row,
    variable_type_id: typeId,
    data: dataArray,
    dataDisplay: dataArray.join(", "),
  };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  // Only allow delete for default tab
  if (activeTab.value !== "default") return;

  if (confirm("Are you sure you want to delete this item?")) {
    try {
      await apiService.delete(API_RESOURCE, id);
      await fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
    } catch (error) {
      console.error("Error deleting item:", error);
    }
  }
};

const handleSaveCreate = async () => {
  if (isCreateFormValid.value) return;

  const { dataDisplay, ...rest } = newItemData.value;
  const payload = {
    ...rest,
    data: rest.data.filter((item) => item.trim().length > 0),
  };

  // Use direct API call since we need to use the base endpoint for creation
  try {
    await apiService.post(API_RESOURCE, payload);
    await fetchModules(1, tablePagination.value.per_page);
    closeCreateModal();
  } catch (error) {
    console.error("Error creating item:", error);
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;

  const { _id, dataDisplay, ...rest } = editingItemData.value;
  const payload = {
    ...rest,
    data: rest.data.filter((item) => item.trim().length > 0),
  };

  // Use direct API call since we need to use the base endpoint for updates
  try {
    await apiService.patch(API_RESOURCE, id, payload);
    await fetchModules(tablePagination.value.current_page, tablePagination.value.per_page);
    closeEditModal();
  } catch (error) {
    console.error("Error updating item:", error);
  }
};

const onCreateModalShown = () => {
  // Focus on title field when modal opens
};

const onEditModalShown = () => {
  // Focus on title field when modal opens
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  isViewMode.value = false;
  newItemData.value = {
    module: MODULE_FILTER,
    title: "",
    description: "",
    is_filterable: false,
    data: [],
    dataDisplay: "",
    variable_type_id: null,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    module: MODULE_FILTER,
    title: "",
    description: "",
    is_filterable: false,
    data: [],
    dataDisplay: "",
    variable_type_id: null,
  };
};

// --- Load data ---
onMounted(async () => {
  await Promise.all([
    fetchModules(tablePagination.value.current_page, tablePagination.value.per_page),
    loadPromptVariables()
  ]);
});
</script>

<style scoped>
/* Modal enhancements */
:deep(.modal-lg) {
  max-width: 800px;
}

:deep(.modal-lg .modal-body) {
  max-height: 70vh;
  overflow-y: auto;
}

/* Form styling */
:deep(.form-group) {
  margin-bottom: 1rem;
}

/* Tags display styling */
:deep(.tags-container) {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

:deep(.tag-item) {
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

:deep(.tag-remove) {
  cursor: pointer;
  color: #dc3545;
  font-weight: bold;
}
 

.nav-tabs .nav-link {
  border-bottom: 2px solid transparent;
  color: #6c757d;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  background-color: transparent;
  border-color: transparent transparent #35bfbf transparent;
  color: #35bfbf;
  font-weight: 700;
}

.nav-tabs .nav-link:hover {
  border-color: transparent transparent #dee2e6 transparent;
  color: #495057;
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}

.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}

.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}

.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}

.page-active:hover {
  background: #21c1d6;
}

.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>
